import { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import toast from "react-hot-toast";
import { useAuthStore } from "./store/authStore";
import {
  initializeEcho,
  joinPresenceChannel,
  getEcho,
} from "./services/echoService";
import { isBiometricEnabled } from "./services/biometricService";
import Layout from "./components/Layout";
import InstallPrompt from "./components/InstallPrompt";
import UpdatePrompt from "./components/UpdatePrompt";
import OfflineIndicator from "./components/OfflineIndicator";
import MobileOnlyScreen from "./components/MobileOnlyScreen";
import BiometricLock from "./components/BiometricLock";
import NotificationPrompt from "./components/NotificationPrompt";
import Login from "./pages/Login";
import ProductRoleSelector from "./components/ProductRoleSelector";
import Home from "./pages/Home";
import QueryList from "./pages/QueryList";
import ChatWindow from "./pages/ChatWindow";
import PlaceholderPage from "./pages/PlaceholderPage";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import AIAssistant from "./pages/AIAssistant";
import Applications from "./pages/Applications";
import ApplicationDocuments from "./pages/ApplicationDocuments";
import DocumentUploadDetail from "./pages/DocumentUploadDetail";
import DisbursementRequest from "./pages/DisbursementRequest";
import ActivityHistory from "./pages/ActivityHistory";
import HelpRequests from "./pages/HelpRequests";

function ProtectedRoute({ children, requireProduct = true }) {
  const token = useAuthStore((state) => state.token);
  const selectedProductId = useAuthStore((state) => state.selectedProductId);
  
  // Check if user is logged in
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if product selection is required and not yet selected
  // This handles both new users and existing users without product selection
  if (requireProduct && !selectedProductId) {
    return <Navigate to="/select-product" replace />;
  }
  
  return children;
}

function App() {
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const [updateRegistration, setUpdateRegistration] = useState(null);
  const [isUnlocked, setIsUnlocked] = useState(() => {
    const biometricEnabled = isBiometricEnabled();
    console.log('🔐 Biometric enabled:', biometricEnabled);
    console.log('🔓 Initial unlock state:', !biometricEnabled);
    return !biometricEnabled;
  });

  useEffect(() => {
    // Listen for service worker updates
    const handleUpdate = (event) => {
      // Don't show update prompt if we just updated (check sessionStorage)
      if (sessionStorage.getItem('sw-just-updated')) {
        sessionStorage.removeItem('sw-just-updated');
        return;
      }
      setUpdateRegistration(event.detail);
    };

    window.addEventListener("sw-update-available", handleUpdate);

    return () => {
      window.removeEventListener("sw-update-available", handleUpdate);
    };
  }, []);

  useEffect(() => {
    // Initialize Echo and connect to presence channel if user is already logged in
    if (token && user?.id) {
      console.log("User already logged in, connecting to presence channel...");
      console.log("Token:", token);
      console.log("User ID:", user.id);

      try {
        // Initialize Echo (will return existing instance if already initialized)
        const echo = initializeEcho(token);
        console.log("Echo instance:", echo);

        // Join private channel for global notifications
        console.log(`Joining private channel for user: ${user.id}`);
        const channel = joinPresenceChannel(user.id, {
          onUpdate: (event) => {
            console.log("Global notification received:", event);

            // Show toast notification based on event type
            if (event.type === "QUERY_MESSAGE") {
              toast(event.message || "New message received", {
                icon: "🔔",
                duration: 4000,
              });
            } else if (event.type === "QUERY_MESSAGE_CLOSED") {
              toast("Query closed", {
                icon: "✓",
                duration: 3000,
              });
            } else if (event.message) {
              toast(event.message, {
                icon: "🔔",
                duration: 4000,
              });
            }

            // Play notification sound
            const audio = new Audio("/sounds/bell.mp3");
            audio.play().catch((e) => console.warn("Audio play failed:", e));
          },
          here: () => {
            console.log("✅ Successfully joined private channel!");
          },
          error: (error) => {
            console.error("❌ Private channel error:", error);
          },
        });

        console.log("Channel object:", channel);

        // Cleanup on unmount or when user/token changes
        return () => {
          console.log("Cleaning up presence channel...");
          channel?.unsubscribe();
        };
      } catch (error) {
        console.error("❌ Failed to initialize Echo or join channel:", error);
      }
    }
  }, [token, user]);

  return (
    <>
      {!isUnlocked && <BiometricLock onUnlock={() => setIsUnlocked(true)} />}
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route 
            path="/select-product" 
            element={
              <ProtectedRoute requireProduct={false}>
                <ProductRoleSelector />
              </ProtectedRoute>
            } 
          />

          {/* Routes without bottom navigation */}
          <Route
            path="/doc-upload"
            element={
              <ProtectedRoute>
                <Applications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/doc-upload/:applicationId"
            element={
              <ProtectedRoute>
                <DocumentUploadDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/doc-upload/:applicationId/:queryId"
            element={
              <ProtectedRoute>
                <DocumentUploadDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/applications/:applicationId"
            element={
              <ProtectedRoute>
                <ApplicationDocuments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/applications/:applicationId/disbursement-request"
            element={
              <ProtectedRoute>
                <DisbursementRequest />
              </ProtectedRoute>
            }
          />
          <Route
            path="/activity-history"
            element={
              <ProtectedRoute>
                <ActivityHistory />
              </ProtectedRoute>
            }
          />

          {/* Routes with Layout (includes bottom navigation) */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={<Home />} />
            <Route path="/ai-assistant" element={<AIAssistant />} />
            <Route path="/queries" element={<QueryList />} />
            <Route path="/chat/:queryId" element={<ChatWindow />} />
            <Route
              path="/applications"
              element={
                <PlaceholderPage
                  title="Applications"
                  description="View and manage all your applications in one place."
                />
              }
            />
            <Route
              path="/deviations"
              element={
                <PlaceholderPage
                  title="Deviations"
                  description="Review and approve deviation requests."
                />
              }
            />
            <Route
              path="/requests"
              element={
                <PlaceholderPage
                  title="Requests"
                  description="Handle pending requests and approvals."
                />
              }
            />
            <Route
              path="/analytics"
              element={
                <PlaceholderPage
                  title="Analytics"
                  description="View detailed performance insights and reports."
                />
              }
            />
            <Route
              path="/team"
              element={
                <PlaceholderPage
                  title="Team"
                  description="Manage your team members and assignments."
                />
              }
            />
            <Route
              path="/notifications"
              element={
                <PlaceholderPage
                  title="Notifications"
                  description="View all your notifications and alerts."
                />
              }
            />
            <Route path="/settings" element={<Settings />} />
            <Route path="/help-requests" element={<HelpRequests />} />
            <Route
              path="/more"
              element={
                <PlaceholderPage
                  title="More"
                  description="Explore additional features and options."
                />
              }
            />
            <Route path="/profile" element={<Profile />} />
          </Route>
        </Routes>
      </Router>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: "#1f4037",
            color: "#fff",
            fontSize: "14px",
            fontWeight: "600",
            borderRadius: "12px",
            padding: "12px 20px",
          },
        }}
      />
      <InstallPrompt />
      <UpdatePrompt
        registration={updateRegistration}
        onClose={() => setUpdateRegistration(null)}
      />
      <OfflineIndicator />
      <MobileOnlyScreen />
      {/* Show notification prompt only when user is logged in and unlocked */}
      {token && isUnlocked && <NotificationPrompt />}
    </>
  );
}

export default App;
